/* eslint-disable no-restricted-imports */
import React, {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import {GameEndData, VictoryPointStateReader} from '@colonist/game-base-types'
import {EndGamePlayerState, PlayerColor} from '@colonist/game-states'
import {PlayerValidator} from '@colonist/game-mechanic-validators'
import {BaseRankedUserState, RankedUserStateController} from '@colonist/leaderboard-types'
import {useUIGameManagerContext} from './UIGameManagerContext'
import {TranslationObject} from '@colonist/i18n'
import {UserSessionUtils} from '../../../../code/client/shared/scripts/UserSessionUtils'
import {ViewPopupKeepPlayingOnWeb} from '../../../../code/client/shared/scripts/ui_popup/ViewPopupKeepPlayingOnWeb'
import {StorageWrapper} from '../utils/storage'
import {ViewPopupChangeOnlineUsername} from '../../../../code/client/shared/scripts/ui_popup/ViewPopupChangeOnlineUsername'
import {FeedbackFormUtils} from '../../../../code/client/shared/scripts/ui_popup/ui_popup_feedback_form/FeedbackFormUtils'
import {socket} from '../../../../code/client/shared/scripts/EntryShared'
import {FeedbackFormLocationEnum} from '../../../../code/shared/entity_datas/EntityDatas'
import {UIFeedbackFormPopupController} from '../../../../code/client/shared/scripts/ui_popup/ui_popup_feedback_form/UIFeedbackFormPopupController'
import {
    ViewPopupMembershipIncentiveEndgame,
} from '../../../../code/client/shared/scripts/ui_popup/ui_popup_membership_incentive/ViewPopupMembershipIncentiveEndgame'
import {CA} from '../../../../code/client/shared/scripts/analytics/Analytics'
import {getHomepageUrl, getHomepageUrlWithParameter} from '../../../../code/client/shared/scripts/ui_utils/ClientUtils'
import {UIUrlParameterNames} from '../../../../code/client/shared/scripts/ui_html/UIUrlController'
import {UIEntryUrlParameterController} from '../../../../code/client/shared/scripts/UIEntryUrlParameterController'
import {UIUtilsEjsVariables} from '../../../../code/client/shared/scripts/UIUtilsEjsVariables'
import {DurationConverter, TimeUtils} from '@colonist/utils'
import {GameStateEnums} from '../../../../code/shared/GameEnums'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums

export const UIGameEndContext = createContext<{
    playersByRank: (EndGamePlayerState & { totalVictoryPoints: number })[]
    gameEndData: GameEndData
    toggleEndGameScreen: (open: boolean) => void
    playerValidatorsByColor: Map<PlayerColor, PlayerValidator>
    currentPlayerValidator: PlayerValidator | undefined
    currentPlayerEndgameState: EndGamePlayerState | undefined
    currentPlayerRankedState: BaseRankedUserState | undefined
    isRankedGame: boolean
    userRankedStatesByColor: {[key in PlayerColor]?: BaseRankedUserState}
    winningPlayer: PlayerValidator | null
    isCurrentPlayerWinner: boolean
    isAfterLiveGame: boolean
    isPlayerHasRankedState: boolean
    exitGameAndGoToHome: () => void
    showPopupsBeforeExitingGame: (continuationAction: () => void, continuationText: TranslationObject) => void
    setIsFirstClickExitGame: (isFirstClickExitGame: boolean) => void
}>({
            gameEndData: {} as GameEndData,
            playersByRank: [],
            playerValidatorsByColor: new Map(),
            currentPlayerValidator: {} as PlayerValidator,
            currentPlayerEndgameState: {} as EndGamePlayerState,
            toggleEndGameScreen: () => null,
            currentPlayerRankedState: undefined,
            isRankedGame: false,
            userRankedStatesByColor: {},
            winningPlayer: null,
            isCurrentPlayerWinner: false,
            isAfterLiveGame: false,
            isPlayerHasRankedState: false,
            exitGameAndGoToHome: () => null,
            showPopupsBeforeExitingGame: () => null,
            setIsFirstClickExitGame: () => null,
        })

export const useUIGameEndContext = () =>
    useContext(UIGameEndContext)

interface UIGameEndProviderProps {
    gameEndData: GameEndData
    children: ReactNode
    isAfterLiveGame: boolean
}

export default function UIGameEndProvider({
    gameEndData,
    children,
    isAfterLiveGame,
}: UIGameEndProviderProps) {
    const gameManager = useUIGameManagerContext()
    const [isFirstClickExitGame, setIsFirstClickExitGame] = useState(true)

    const shouldShowShareActivityLinkPopup = () => {
        if(!UIUtilsEjsVariables.isUserOnDiscord) return false

        const lastPopupTime = StorageWrapper.getLastShareActivityLinkPopupTime()
        if(lastPopupTime == null) return true

        const millisecondsSinceLastPopupTime = TimeUtils.millisecondsPassedSince(lastPopupTime.getTime())
        const shownWithinLastDay = millisecondsSinceLastPopupTime < DurationConverter.daysAsMilliseconds(1)

        return !shownWithinLastDay
    }

    const showPopupsBeforeExitingGame = (continuationAction: () => void,
                                         continuationText: TranslationObject) => {
        if(!isFirstClickExitGame) return continuationAction()

        const shouldShowDiscordKeepPlayingOnWebPopup = UserSessionUtils.canShowDiscordKeepPlayingOnWebPopup()
        if(shouldShowDiscordKeepPlayingOnWebPopup) {
            new ViewPopupKeepPlayingOnWeb(continuationAction)
            StorageWrapper.setDiscordKeepPlayingOnWebPopupShown()
            return
        }

        const shouldShowChangeUsernamePopup =
         UserSessionUtils.shouldShowChangeUsernamePopup(gameManager.gameController.gameDetails.matchmakingMatchType)
        if(shouldShowChangeUsernamePopup) {
            new ViewPopupChangeOnlineUsername(continuationAction)
            StorageWrapper.setDiscordOnlineUsernamePopupShown()
            return
        }

        const shouldShowFeedbackForm = FeedbackFormUtils.shouldShowFeedbackForm()
        if(shouldShowFeedbackForm) {
            new UIFeedbackFormPopupController(continuationAction, socket.gameListener.databaseGameId, FeedbackFormLocationEnum.EndGame)
            return
        }

        const shouldShowMembershipIncentive = UserSessionUtils.canShowSubscriptionPopup()
        if(shouldShowMembershipIncentive) {
            new ViewPopupMembershipIncentiveEndgame(continuationAction, gameManager.gameController.gameSettings, continuationText)
            return
        }

        continuationAction()
    }

    const exitGameAndGoToHome = () => {
        void showPopupsBeforeExitingGame(() => {
            CA.sendGoogleAnalyticsEvent(CA.Category.EndGame, CA.Label.ClickedEndGameLobby)
            const redirectUrl = shouldShowShareActivityLinkPopup()
                ? getHomepageUrlWithParameter(UIUrlParameterNames.DiscordShareActivity,
                    UIEntryUrlParameterController.DEFAULT_PARAMETER_VALUE)
                : getHomepageUrl()

            window.history.pushState('', document.title, redirectUrl)
            location.reload()
        }, {key: 'strings:game.endGame.footer.home'})
        setIsFirstClickExitGame(false)
    }

    const closeEndGameScreen = () => {
        gameManager.mapController.setInteractionEnabled(true)
        gameManager.eventController.sendEvent(undefined, UIGameEventEnums.GameEndShowMap)
        const gameSettingsContainer = document.getElementById('ui-game-end-container')
        if(gameSettingsContainer) {
            gameSettingsContainer.style.visibility = 'hidden'
        }
    }

    const openEndGameScreen = () => {
        gameManager.mapController.setInteractionEnabled(false)
        gameManager.eventController.sendEvent(undefined, UIGameEventEnums.GameEndHideMap)
        const gameSettingsContainer = document.getElementById('ui-game-end-container')
        if(gameSettingsContainer) {
            gameSettingsContainer.style.visibility = 'visible'
        }
    }

    useEffect(() => {
        gameManager.mapController.setInteractionEnabled(false)
    }, [])

    const toggleEndGameScreen = (open: boolean) => {
        if(open) {
            openEndGameScreen()
        } else {
            closeEndGameScreen()
        }
    }

    const playersByRank = useMemo(() => Object.values(gameEndData.endGameState.players)
        .sort((a, b) => a.rank - b.rank)
        .map((item) => ({
            ...item,
            totalVictoryPoints: VictoryPointStateReader.totalPrivateVictoryPoints(item.victoryPoints),
        })), [gameEndData])

    const playerValidatorsByColor = gameManager.gameController.getPlayerValidatorsMap()

    const currentPlayerValidator = playerValidatorsByColor.get(gameManager.gameController.myColor)

    const currentPlayerEndgameState = gameEndData?.endGameState.players[gameManager.gameController.myColor]

    const {currentPlayerRankedState, userRankedStatesByColor} = useMemo(() => {
        const userRankedStatesByColorMap: {[key in PlayerColor]?: BaseRankedUserState} = {}
        gameEndData?.rankedUserStates?.forEach((rankedUserState) => {
            const deserializedRankedUserState = RankedUserStateController.deserialize(rankedUserState.rankedState)
            userRankedStatesByColorMap[rankedUserState.color] = deserializedRankedUserState
        })
        return {
            currentPlayerRankedState: userRankedStatesByColorMap[gameManager.gameController.myColor],
            userRankedStatesByColor: userRankedStatesByColorMap,
        }
    }, [gameEndData, gameManager])

    const isRankedGame = gameManager.gameController.gameDetails.isRanked
    const isPlayerHasRankedState = Boolean(currentPlayerRankedState)

    const winningPlayerColor = useMemo(() => {
        const players = Object.values(gameEndData.endGameState.players)
        for(const endGameState of players) {
            if(endGameState.winningPlayer === true) {
                return endGameState.color
            }
        }
        return null
    }, [gameEndData])

    const winningPlayer = useMemo(() => {
        if(winningPlayerColor != null) {
            return playerValidatorsByColor.get(winningPlayerColor) || null
        }
        return null
    }, [winningPlayerColor, playerValidatorsByColor])

    const isCurrentPlayerWinner = useMemo(() => {
        if(winningPlayerColor === currentPlayerValidator?.color) {
            return true
        }
        return false
    }, [winningPlayerColor, currentPlayerValidator])

    return (
        <UIGameEndContext.Provider
            value={{
                toggleEndGameScreen: toggleEndGameScreen,
                playersByRank: playersByRank,
                gameEndData: gameEndData,
                playerValidatorsByColor: playerValidatorsByColor,
                currentPlayerValidator: currentPlayerValidator,
                currentPlayerEndgameState: currentPlayerEndgameState,
                currentPlayerRankedState: currentPlayerRankedState,
                isRankedGame: isRankedGame,
                userRankedStatesByColor: userRankedStatesByColor,
                winningPlayer: winningPlayer,
                isCurrentPlayerWinner: isCurrentPlayerWinner,
                isAfterLiveGame: isAfterLiveGame,
                isPlayerHasRankedState: isPlayerHasRankedState,
                exitGameAndGoToHome: exitGameAndGoToHome,
                showPopupsBeforeExitingGame: showPopupsBeforeExitingGame,
                setIsFirstClickExitGame: setIsFirstClickExitGame,
            }}
        >
            {children}
        </UIGameEndContext.Provider>
    )
}
